import React from 'react';
import { motion } from 'framer-motion';
import {
  Upload,
  Sparkles,
  BarChart3,
  FileText,
  ArrowRight,
  Shield,
  Zap,
  Database,
  Play
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 }
};

const Onboarding = ({
  onLoadSample,
  onQuestionSelect,
  onStartUpload,
  sampleLoading,
  sampleMeta,
  isAuthenticated,
  onRequireAuth,
  demoMode
}) => {
  const questions = sampleMeta?.questions || [];

  const features = [
    {
      icon: Shield,
      title: 'Privacy-first',
      description: 'Data stays in your browser'
    },
    {
      icon: Zap,
      title: 'AI-powered',
      description: 'Natural language queries'
    },
    {
      icon: BarChart3,
      title: 'Instant charts',
      description: 'Visualize in seconds'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 rounded-full border border-sage-300 bg-sage-100 px-4 py-2 text-sm text-sage-700 mb-4">
          <Database className="h-4 w-4" />
          Local-first analytics
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-ink mb-3">
          Welcome to DuckPilot
        </h1>
        <p className="text-lg text-sand-700">
          Ask questions in plain English. Get answers with charts. Your data never leaves your browser.
        </p>
      </motion.div>

      {/* Feature Pills */}
      <motion.div
        {...fadeUp}
        transition={{ delay: 0.1 }}
        className="flex flex-wrap justify-center gap-3"
      >
        {features.map((feature, index) => (
          <div
            key={index}
            className="flex items-center gap-2 rounded-full border border-[rgba(32,30,29,0.12)] bg-white px-4 py-2 shadow-brand-sm"
          >
            <feature.icon className="h-4 w-4 text-sage-600" />
            <span className="text-sm text-sand-700">
              <span className="font-medium text-ink">{feature.title}</span>
              <span className="mx-1.5 text-sand-500">·</span>
              {feature.description}
            </span>
          </div>
        ))}
      </motion.div>

      {/* Main Options */}
      <motion.div
        {...fadeUp}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto"
      >
        {/* Demo Option */}
        <div className="rounded-2xl border border-bill-300 bg-gradient-to-br from-bill-100 to-white p-6 shadow-brand-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-bill-500 border border-bill-600">
              <Play className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-ink">Try the demo</h3>
              <p className="text-sm text-sand-600">No account required</p>
            </div>
          </div>

          {demoMode ? (
            <div className="mb-4 rounded-xl border border-[rgba(32,30,29,0.12)] bg-white p-4">
              <div className="flex items-center gap-2 mb-3">
                <Database className="h-4 w-4 text-bill-600" />
                <span className="text-sm font-medium text-ink">
                  Choose a demo dataset
                </span>
              </div>
              <p className="text-xs text-sand-600 mb-3">
                Pick from e-commerce sales, SaaS subscriptions, or support tickets to explore DuckPilot's features.
              </p>
              <div className="text-xs text-sand-600">
                3 datasets available
              </div>
            </div>
          ) : (
            <div className="mb-4 rounded-xl border border-[rgba(32,30,29,0.12)] bg-white p-4">
              <div className="flex items-center gap-2 mb-2">
                <Database className="h-4 w-4 text-bill-600" />
                <span className="text-sm font-medium text-ink">
                  {sampleMeta?.name || 'Sample dataset'}
                </span>
              </div>
              <p className="text-xs text-sand-600 mb-2">
                {sampleMeta?.description || 'Explore with sample data'}
              </p>
              <div className="text-xs text-sand-600">
                {sampleMeta?.rows || 20} rows · {sampleMeta?.columns || 10} columns
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={onLoadSample}
            disabled={!onLoadSample || sampleLoading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-bill-500 px-4 py-3 text-sm font-semibold text-white hover:bg-bill-600 shadow-brand-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sampleLoading ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Loading demo...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                {demoMode ? 'Choose demo dataset' : 'Start exploring'}
              </>
            )}
          </button>
        </div>

        {/* Upload Option */}
        <div className="rounded-2xl border border-[rgba(32,30,29,0.12)] bg-white p-6 shadow-brand-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sand-100 border border-[rgba(32,30,29,0.12)]">
              <Upload className="h-5 w-5 text-sand-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-ink">Upload your data</h3>
              <p className="text-sm text-sand-600">Free account required</p>
            </div>
          </div>

          <div className="mb-4 rounded-xl border border-dashed border-sand-400 bg-sand-100 p-4 text-center">
            <FileText className="h-8 w-8 text-sand-500 mx-auto mb-2" />
            <p className="text-sm text-sand-600 mb-1">
              Drag & drop or click to upload
            </p>
            <p className="text-xs text-sand-500">
              CSV, Excel, JSON, Parquet · Max 50MB
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              if (!isAuthenticated && onRequireAuth) {
                onRequireAuth();
              } else {
                onStartUpload();
              }
            }}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-[rgba(32,30,29,0.18)] bg-white px-4 py-3 text-sm font-semibold text-ink hover:bg-sand-100 transition-colors"
          >
            {isAuthenticated ? (
              <>
                Upload your file
                <ArrowRight className="h-4 w-4" />
              </>
            ) : (
              <>
                Create free account
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </div>
      </motion.div>

      {/* Example Questions */}
      {questions.length > 0 && (
        <motion.div
          {...fadeUp}
          transition={{ delay: 0.3 }}
          className="max-w-3xl mx-auto"
        >
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-4 w-4 text-sand-600" />
            <h3 className="text-sm font-medium text-sand-600">
              Try asking these questions
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {questions.map((question, index) => (
              <button
                key={index}
                type="button"
                onClick={() => onQuestionSelect && onQuestionSelect(question)}
                className="group rounded-xl border border-[rgba(32,30,29,0.12)] bg-white px-4 py-3 text-left text-sm text-sand-700 hover:bg-sand-100 hover:text-ink shadow-brand-sm transition-colors"
              >
                <span className="flex items-center gap-2">
                  <span className="text-sand-500 group-hover:text-bill-600 transition-colors">
                    →
                  </span>
                  {question}
                </span>
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Trust Signal */}
      <motion.div
        {...fadeUp}
        transition={{ delay: 0.4 }}
        className="text-center"
      >
        <div className="inline-flex items-center gap-2 text-xs text-sand-600">
          <Shield className="h-3 w-3 text-sage-600" />
          Your data is processed locally and never uploaded to our servers
        </div>
      </motion.div>
    </div>
  );
};

export default Onboarding;
